// Recovering a single-document or bulk save that the local store refused.
//
// Extracted from DatabaseWrapper so it can be unit-tested without pulling in
// pouchdb-browser, which references `self` and fails to load under node. It was
// split out of database-wrapper-passthrough.ts, which had become the shared home
// for every testable sync helper regardless of subject (deferred finding
// df-08d4a072f17b).

import { isUsableDocId } from '@cf-shared/deep-helpers';
import { PouchDoc, isPouchConflict, isPouchNotFound } from '@cf-shared/data-model/pouch';
import { DbHandle } from './database-wrapper-passthrough';
import { DatabaseRecoverySettlement } from './singleton-read-recovery';

/**
 * #5129 — which rung of the save ladder lost a write to a database repair.
 *
 * An enum rather than free text because the value is also sent to Sentry as a TAG, so a
 * person can break the one issue down by rung. Tags must be low-cardinality, and a fixed
 * list of names is what keeps it that way. The values name the METHOD and the step inside
 * it, because that is what someone reading the Sentry issue has to go and look at.
 */
export enum WriteDropSite {
  /** saveRecordInternalAsync: the very first db.put of the save */
  FirstPut = 'first-put',
  /** retryPutWithMergeAsync: reading the current doc to merge against */
  MergeRetryRead = 'merge-retry-read',
  /** retryPutWithMergeAsync: the put that writes the merged document */
  MergeRetryPut = 'merge-retry-put',
  /** recoverVanishedDocConflictAsync: retrying the save as a fresh create */
  VanishedDocFreshCreate = 'vanished-doc-fresh-create',
  /** recoverVanishedDocConflictAsync: reading the doc that arrived via sync */
  VanishedDocRead = 'vanished-doc-read',
  /** recoverVanishedDocConflictAsync: the put after merging onto that doc */
  VanishedDocMergePut = 'vanished-doc-merge-put',
  /** recoverPutOnDeletedOrAbsentDocAsync: the fresh create over a tombstone */
  DeletedDocFreshCreate = 'deleted-doc-fresh-create',
  /** recoverPutOnDeletedOrAbsentDocAsync: reading the doc that arrived via sync */
  DeletedDocRead = 'deleted-doc-read',
  /** recoverPutOnDeletedOrAbsentDocAsync: the put after merging onto that doc */
  DeletedDocMergePut = 'deleted-doc-merge-put',
  /** recoverDeleteOnConflictAsync: re-probing the current winner */
  DeleteConflictRead = 'delete-conflict-read',
}

/** The one Sentry issue every dropped write reports under. Stable on purpose: the docId and
 *  the rung go in the context and the tag, not in the message, so per-document repeats
 *  collapse into a single issue instead of opening thousands. */
export const WRITE_DROPPED_INTO_REPAIR_PROBLEM = 'Db: write dropped into graceful database repair';

/**
 * How a write attempt ended (df-068808a2796e).
 *
 * Every rung of the save ladder used to answer this question with a boolean, and that
 * boolean had to mean two different things at once: `true` was "the record was written"
 * AND "the failure was taken over by the database recovery, so stop worrying about it".
 * Those are not the same outcome. When `interceptDatabaseError` takes a failure, NOTHING
 * WAS WRITTEN — the recovery may repair the database in place and let the page carry on,
 * and then the write is simply gone.
 *
 * For an ordinary save that conflation is deliberate and stays: the in-memory record still
 * holds the user's edit, so their next save of the same object carries it into the database
 * either way. A DELETE has no next save — nothing re-saves a deleted object — and worse, the
 * delete left `_deleted` standing on the shared object, so the user's next ordinary save
 * writes a tombstone carrying their newest content. That is why the two answers have to be
 * told apart at all, and why only the delete paths act on the difference.
 *
 * `Lost` is the old `false`: the write is genuinely gone and the CALLER surfaces the drop.
 */
export enum WriteOutcome {
  /** The record reached the database. */
  Written = 'written',
  /** A failure was taken over by `interceptDatabaseError`. Nothing was written, and no
   *  error is travelling to the caller to say so. */
  DroppedIntoRecovery = 'dropped-into-recovery',
  /** The write failed and nothing recovered it. The caller surfaces the drop. */
  Lost = 'lost',
}

/** What reportWriteDroppedIntoGracefulRecovery needs, injected for the same reason
 *  everything else in this file is: it has to be testable without DatabaseWrapper. */
export interface WriteDropReportDeps {
  /** DatabaseRecoveryService.whenDatabaseRecoverySettledAsync — `RepairedInPlace` means the
   *  database was repaired in place and the app is carrying on; the other two answers
   *  mean the page is being replaced, or nothing repaired anything and nothing will
   *  reload either. */
  whenRecoverySettled: () => Promise<DatabaseRecoverySettlement>;
  /** LoggingService.trackSilentProblem. */
  trackSilentProblem: (name: string, context: Record<string, unknown>, tags?: Record<string, string>) => void;
}

/**
 * #5129 — report a write that `interceptDatabaseError` swallowed, but ONLY when the
 * recovery it was swallowed into repaired the database in place.
 *
 * `interceptDatabaseError` answering `true` means "I have taken this failure", and every
 * write path treats that as "saved". Two very different things hide behind that one answer:
 *
 * - The recovery RELOADS the page, or nothing runs at all. The write was never going to
 *   land, and those paths have their own telemetry. Reporting here would be pure noise,
 *   so this stays silent — the same split the read path already makes for a singleton whose
 *   boot read was intercepted.
 * - The recovery repairs the database IN PLACE (a graceful recovery) and the app
 *   deliberately carries on. There is no reload to excuse the loss: the user's edit is
 *   gone, the caller was told it was saved, and until #5129 nothing recorded it. That is
 *   what this reports.
 *
 * `trackSilentProblem` rather than `trackSilentInfo` because nothing repaired the write —
 * the condition is real and unresolved, and a person may eventually need to act on it. The
 * user is deliberately not interrupted, because there is nothing useful to ask them to do.
 *
 * Deliberately fire-and-forget. The caller is mid-save, usually holding the write mutex,
 * and a report must never delay a save or change what it answers. It also must never throw
 * into a save path, so the promise carries its own catch.
 *
 * There is deliberately NO "was this permanently lost?" field. A dropped write is not
 * necessarily gone for good: the in-memory record still holds the user's edit, so the next
 * save of that same object carries it into the database either way — with a revision the
 * save conflicts and the merge path folds the edit in, and without one it goes out as a
 * fresh create. Whether that next save ever happens is the only thing that decides
 * permanence, and it is not knowable here. `site` already says which rung lost the write,
 * which is what triage can actually act on.
 *
 * `DeleteConflictRead` is the rung that paragraph does NOT describe, and it is different in
 * both directions. A deletion has no later save to carry it in — nothing re-saves a deleted
 * object — so a deletion genuinely lost here stays lost. But that rung is also the one that
 * can report when nothing was lost at all: it is reached because the re-probe READ failed,
 * so whether the local winner was already a tombstone is exactly what could not be
 * established. Read its count as an upper bound, not as a count of lost deletions.
 */
export function reportWriteDroppedIntoGracefulRecovery(
  deps: WriteDropReportDeps,
  docId: string,
  site: WriteDropSite,
): void {
  void (async () => {
    // wait for the recovery this failure was taken into, then report only the case where
    // the app carried on with the same stored database. Every other answer stays silent,
    // including "nothing repaired anything and nothing will reload" (df-4ebecd016a94) —
    // that arm is a call on a handle the app closed itself, so the write was addressed to a
    // database that had already gone, and the arm reports itself.
    if (await deps.whenRecoverySettled() !== DatabaseRecoverySettlement.RepairedInPlace) {
      return;
    }
    deps.trackSilentProblem(
      WRITE_DROPPED_INTO_REPAIR_PROBLEM,
      { docId, site },
      { site },
    );
  })().catch(() => {
    // reporting a lost write must never itself break the save path that lost it
  });
}

/** Dependencies for recoverMissingDocViaReadDbAsync, injected so the
 *  recovery decision is a pure function we can unit test (DatabaseWrapper
 *  itself cannot be loaded under node — see the module comment above). */
export interface MissingDocReadDbRecoveryDeps {
  /** The DB reads currently route to (DatabaseWrapper.getReadDb()). */
  readDb: DbHandle | null;
  /** The DB the failing write went to (the caller's write db). */
  writeDb: DbHandle | null;
  /** Fetch a doc from the read db. */
  getFromReadDb: (docId: string) => Promise<PouchDoc>;
  /** Graft docs into local PouchDB (DatabaseWrapper.graftPassThroughDocs,
   *  with watcher notification off — the caller is mid-write, and the
   *  write's own completion path handles cache + watcher updates). */
  graftToLocal: (docs: PouchDoc[]) => Promise<void>;
  log: (message: string) => void;
  /** Report a recovery read that failed with anything but not-found (#5191). For a live
   *  record the caller goes on to save anyway, so this report is the only record that the
   *  read failed (see ReadDbRecoveryAnswer.ReadFailed). */
  reportReadFailure: (docId: string, err: unknown) => void;
}

/** How recoverMissingDocViaReadDbAsync ended (df-4ebed2f69e4a). */
export enum ReadDbRecoveryAnswer {
  /** The read db had the doc. It is grafted into local and handed back for the merge. */
  Recovered = 'recovered',
  /** The doc is absent as far as anything can tell: there is no read/write split (so the
   *  local not-found is the whole answer), or the read db said not-found too. */
  Absent = 'absent',
  /** The read db could not be asked: the read failed with something other than not-found
   *  (network, auth). Whether the server holds the doc live is UNKNOWN. */
  ReadFailed = 'read-failed',
}

export type ReadDbRecoveryResult =
  | { answer: ReadDbRecoveryAnswer.Recovered; doc: PouchDoc }
  | { answer: ReadDbRecoveryAnswer.Absent }
  | { answer: ReadDbRecoveryAnswer.ReadFailed };

/**
 * Write-path recovery for the initial-sync pass-through window (#3987
 * follow-up — the canary "saveBulkAsync: write dropped" storm).
 *
 * The failure shape: while the user db's initial sync is still running,
 * reads route to the REMOTE db but writes go to LOCAL. A record read (or
 * derived from state read) through the remote can carry a revision the
 * local db has not pulled yet. Saving it to local then fails with a 409
 * conflict, and the merge-retry's local `get` finds no doc at all —
 * before this recovery, that write was reported as dropped even though
 * the doc exists on the remote and the write is perfectly recoverable.
 *
 * The recovery: fetch the CURRENT doc from the read db and graft it into
 * local with `bulkDocs({new_edits: false})` (the same primitive every
 * pass-through read uses — see graftPassThroughDocs in
 * database-wrapper-passthrough.ts). After the
 * graft, local has the doc's revision tree, so the caller can merge the
 * in-memory record onto the fetched doc and retry the put as a normal
 * rev-chain extension.
 *
 * Answers `Recovered` with the fetched doc when that worked. Otherwise:
 *   - `Absent` when there is no read/write split (window closed, or coach
 *     impersonation where both roles point at temporaryDb), or when the
 *     read db does not have the doc either (deleted everywhere). The
 *     caller then tries its deleted-or-absent recovery.
 *   - `ReadFailed` when the read db could not be asked (reported through
 *     `reportReadFailure` first). The caller treats a deletion and a live
 *     record differently here, because "absent" was never established: see
 *     the not-found branch of retryPutWithMergeAsync in database-wrapper.ts
 *     and design-docs/pass-through-db.md, "A recovery read that fails".
 */
export async function recoverMissingDocViaReadDbAsync(
  deps: MissingDocReadDbRecoveryDeps,
  docId: string,
): Promise<ReadDbRecoveryResult> {
  // only meaningful while reads and writes route to DIFFERENT dbs (the
  // pass-through window). identity comparison mirrors chooseReadDb /
  // chooseWriteDb in database-wrapper-passthrough.ts.
  if (!deps.readDb || deps.readDb === deps.writeDb) {
    return { answer: ReadDbRecoveryAnswer.Absent };
  }
  let currentDoc: PouchDoc;
  try {
    currentDoc = await deps.getFromReadDb(docId);
  } catch (readErr) {
    // nothing to recover from. two sub-cases, answered differently:
    // - if the read db said not-found, the doc is deleted / gone everywhere.
    //   stay quiet: it is an answer, not a failure;
    // - otherwise (network / auth / unexpected failure) the read db could not
    //   be asked at all. report it, and say so to the caller, which must not
    //   read it as "gone everywhere".
    if (isPouchNotFound(readErr)) {
      return { answer: ReadDbRecoveryAnswer.Absent };
    }
    deps.log(`recoverMissingDocViaReadDbAsync: recovery read for ${docId} failed (not a not-found): `
      + `${(readErr as any)?.message || readErr}`);
    deps.reportReadFailure(docId, readErr);
    return { answer: ReadDbRecoveryAnswer.ReadFailed };
  }
  await deps.graftToLocal([currentDoc]);
  deps.log(`retryPutWithMergeAsync: ${docId} was missing locally during the initial-sync pass-through window — `
    + `grafted current doc (rev ${currentDoc._rev}) from the read db and retrying the merge`);
  return { answer: ReadDbRecoveryAnswer.Recovered, doc: currentDoc };
}

/** Dependencies for recoverPutOnDeletedOrAbsentDocAsync, injected so the
 *  recovery flow is a pure function we can unit test (DatabaseWrapper
 *  itself cannot be loaded under node — see the module comment above). */
export interface DeletedDocPutRecoveryDeps extends WriteDropReportDeps {
  /** Put a record to the caller's write db. */
  putToWriteDb: (record: PouchDoc) => Promise<unknown>;
  /** Fetch a doc from the caller's write db. */
  getFromWriteDb: (docId: string) => Promise<PouchDoc>;
  /** The wrapper's interceptDatabaseError — true means the failure was
   *  handled (e.g. an IndexedDB loss that triggers the recovery reload). */
  interceptDatabaseError: (err: unknown) => boolean;
  /** Merge the in-memory record onto the db's current doc and stamp the
   *  db's _rev onto it (the wrapper's mergeRecordAgainstStoredDoc +
   *  copyDocumentData + rev assignment — the same steps the normal
   *  merge-retry path performs). */
  applyMergeOntoDbDoc: (record: PouchDoc, dbDoc: PouchDoc) => void;
  /** Post-save bookkeeping (the wrapper's applyPutResponseToCache). */
  onSaved: (record: PouchDoc, putResponse: unknown) => void;
  /** Clear the identity-map's expected rev for a doc whose stored revision
   *  no longer applies — either the fresh create restarts the local revision
   *  chain, or a deletion found its target already gone. */
  clearExpectedRev: (docId: string) => void;
  log: (message: string) => void;
  logError: (message: string) => void;
}

/**
 * Write-path recovery v2 for a put that conflicted while the local doc is
 * deleted or absent (#3987 follow-up — the canary tombstone shape,
 * `reason=deleted`). Runs AFTER recoverMissingDocViaReadDbAsync could not
 * supply the doc (no read/write split, the read db said not-found, or — for
 * a live record only — the read db could not be asked; a deletion in that
 * last case never reaches here, see ReadDbRecoveryAnswer.ReadFailed).
 *
 * The failure shape: early in a boot the app creates log entries fresh
 * (their first save lands with a gen-1 rev); moments later pull
 * replication delivers TOMBSTONES for those very ids (deleted in an
 * earlier session on another device / before a user switch). The next
 * save of the in-memory record then 409s (its rev extends a branch whose
 * winner is now deleted), and the merge-retry's `get` fails with
 * reason=deleted — before this recovery, every such row was reported as
 * a dropped write.
 *
 * The recovery is adapted from what the single-doc path has always done
 * in `recoverVanishedDocConflictAsync` (database-wrapper.ts) — the same
 * fresh-create-then-merge-follow-up shape — with two deliberate
 * differences: it returns an outcome instead of throwing (one bad bulk
 * row must not abort its siblings), and it short-circuits a `_deleted`
 * record to a success no-op where the single-doc helper fresh-creates
 * the tombstone (same end state, different mechanics). If you change
 * one of the two, check the other. Note the trigger is ANY not-found —
 * both a deleted winner (reason=deleted, the canary tombstone shape)
 * and a genuinely absent doc (reason=missing) are healed:
 *   - a `_deleted` record hitting an already-deleted/absent doc is a
 *     SUCCESS no-op — the deletion's goal state is already reached;
 *   - a live record is retried as a FRESH CREATE (clear `_rev`, put).
 *     PouchDB explicitly supports a rev-less create over a deleted
 *     winner — it grafts the new revision onto the tombstone; on an
 *     absent doc it is a plain create;
 *   - if the fresh create itself conflicts, a live doc arrived via sync
 *     between the failed `get` and our put — merge onto it once and put
 *     again.
 *
 * Answers `Written` when the write landed (or the deletion's goal state was
 * already reached), `DroppedIntoRecovery` when a failure was taken over by the
 * database recovery and nothing was written, and `Lost` when the write is
 * genuinely gone — the CALLER then surfaces the drop through its existing
 * error + telemetry path. Before df-068808a2796e the first two shared one
 * `true`, which is what let a swallowed delete answer as a success.
 */
export async function recoverPutOnDeletedOrAbsentDocAsync(
  deps: DeletedDocPutRecoveryDeps,
  record: PouchDoc,
  attemptedRev: string | undefined,
): Promise<WriteOutcome> {
  // if the record is itself a deletion, the doc being already deleted or
  // absent means the goal state is reached — success, nothing to write.
  // clear the identity-map's expected rev too: it points at a revision of
  // a doc that no longer exists, same as in the fresh-create branch below.
  if (record._deleted) {
    deps.clearExpectedRev(record._id);
    deps.log(`retryPutWithMergeAsync: delete of ${record._id} is a no-op — the doc is already deleted/absent locally`);
    return WriteOutcome.Written;
  }

  // retry the live record as a fresh create — PouchDB grafts a rev-less
  // create onto a deleted winner, so this succeeds over a tombstone
  deps.log(`retryPutWithMergeAsync: ${record._id} conflicted but the local doc is deleted/absent — `
    + `retrying as fresh create (attemptedRev=${attemptedRev})`);
  delete record._rev;
  deps.clearExpectedRev(record._id);
  let freshCreateErr: unknown;
  try {
    const response = await deps.putToWriteDb(record);
    deps.onSaved(record, response);
    return WriteOutcome.Written;
  } catch (err) {
    freshCreateErr = err;
  }
  if (deps.interceptDatabaseError(freshCreateErr)) {
    // the failure was taken over, not written. Report it if the database was repaired in
    // place (#5129), because then nothing else ever will — and answer DroppedIntoRecovery
    // rather than Written, so a delete on this rung is not told it succeeded
    // (df-068808a2796e).
    reportWriteDroppedIntoGracefulRecovery(deps, record._id, WriteDropSite.DeletedDocFreshCreate);
    return WriteOutcome.DroppedIntoRecovery;
  }
  if (!isPouchConflict(freshCreateErr)) {
    // a non-conflict failure (e.g. an IndexedDB write error) — nothing
    // further to try; the caller surfaces the drop
    deps.logError(`retryPutWithMergeAsync: fresh-create retry failed (non-conflict) for ${record._id}: `
      + `${(freshCreateErr as any)?.message || freshCreateErr}`);
    return WriteOutcome.Lost;
  }

  // the fresh create also conflicted: a live doc arrived via sync between
  // the failed get and our put — fetch it and merge our record onto it
  let syncedDoc: PouchDoc;
  try {
    syncedDoc = await deps.getFromWriteDb(record._id);
  } catch (getErr) {
    if (deps.interceptDatabaseError(getErr)) {
      reportWriteDroppedIntoGracefulRecovery(deps, record._id, WriteDropSite.DeletedDocRead);
      return WriteOutcome.DroppedIntoRecovery;
    }
    // conflicted again but still unreadable (e.g. deleted again mid-race) —
    // give up and let the caller surface the drop
    deps.logError(`retryPutWithMergeAsync: fresh-create conflicted for ${record._id} but the current doc is `
      + `still unreadable: ${(getErr as any)?.message || getErr}`);
    return WriteOutcome.Lost;
  }
  deps.applyMergeOntoDbDoc(record, syncedDoc);
  try {
    const response = await deps.putToWriteDb(record);
    deps.onSaved(record, response);
    return WriteOutcome.Written;
  } catch (finalErr) {
    if (deps.interceptDatabaseError(finalErr)) {
      reportWriteDroppedIntoGracefulRecovery(deps, record._id, WriteDropSite.DeletedDocMergePut);
      return WriteOutcome.DroppedIntoRecovery;
    }
    deps.logError(`retryPutWithMergeAsync: merge follow-up after fresh-create conflict failed for ${record._id}: `
      + `${(finalErr as any)?.message || finalErr}`);
    return WriteOutcome.Lost;
  }
}

/** Dependencies for recoverDeleteOnConflictAsync, injected so the recovery
 *  decision is a pure function we can unit test (DatabaseWrapper itself cannot
 *  be loaded under node — see the module comment at the top of this file). A
 *  narrower set than DeletedDocPutRecoveryDeps: a delete-goal-reached recovery
 *  never merges or writes, so it needs neither putToWriteDb, onSaved nor
 *  applyMergeOntoDbDoc. */
export interface DeleteConflictRecoveryDeps extends WriteDropReportDeps {
  /** Fetch the current winning doc from the caller's write db. */
  getFromWriteDb: (docId: string) => Promise<PouchDoc>;
  /** The wrapper's interceptDatabaseError — true means the failure was
   *  handled (e.g. an IndexedDB loss that triggers the recovery reload). */
  interceptDatabaseError: (err: unknown) => boolean;
  /** Clear the identity-map's expected rev for a doc whose stored revision no
   *  longer wins (the deletion's target is already gone). */
  clearExpectedRev: (docId: string) => void;
  log: (message: string) => void;
  logError: (message: string) => void;
}

/**
 * Write-path recovery for a DELETION whose merge-retry put conflicted with a
 * 409 (nightly #4010 follow-up — the "Cookie Conflict on User Switch" WeighIn
 * write-drop).
 *
 * The failure shape: a bulk-delete row 409s, so it enters the shared
 * merge-retry. The local `get` finds the doc already deleted, so v1
 * (recoverMissingDocViaReadDbAsync) fetches the STILL-LIVE remote doc and grafts
 * it — but the graft is a no-op, because that remote rev is already a NON-LEAF
 * ancestor of the local tombstone. The merge-retry then re-attempts the delete
 * at that non-leaf rev and 409s again. Because v1 returned a doc (non-null), the
 * v2 deleted/absent recovery in the get-catch never ran; and the put-catch's v2
 * call excludes `_deleted` records, so the write was reported as a drop — even
 * though the doc IS deleted locally and that tombstone will sync to the remote.
 *
 * The recovery re-probes the CURRENT local winner (the put-catch cannot assume
 * the doc is absent — its 409 only proves the winner sits at some rev the stale
 * delete could not extend) and classifies:
 *   - winner deleted/absent  -> the deletion's goal state is already reached.
 *     Success no-op: nothing to write, the local tombstone is a pending local
 *     edit that sync pushes to the remote. Clear the identity-map's expected
 *     rev (it points at a rev that no longer wins).
 *   - winner LIVE            -> the deletion genuinely did NOT land (a newer
 *     live rev arrived via sync). Answer `Lost` so the caller SURFACES the drop,
 *     matching the pre-existing "un-landed deletions must surface" policy. We
 *     deliberately do NOT blind-delete the live winner here: that could clobber
 *     a concurrent edit, a design decision that belongs to the caller, not this
 *     silent recovery. The classification is the not-found-vs-resolved outcome
 *     of the re-probe get; `current` itself is only read to log the live
 *     winner's rev, and nothing is written on any path.
 *
 * Answers `Written` only for the already-deleted case; `DroppedIntoRecovery` for an
 * intercepted failure; `Lost` for a live winner or an unexpected (non-not-found)
 * read failure, so a genuinely un-landed deletion is never falsely reported as
 * recovered. The first two shared one `true` until df-068808a2796e.
 */
export async function recoverDeleteOnConflictAsync(
  deps: DeleteConflictRecoveryDeps,
  record: PouchDoc,
  attemptedRev: string | undefined,
): Promise<WriteOutcome> {
  let current: PouchDoc;
  try {
    current = await deps.getFromWriteDb(record._id);
  } catch (getErr) {
    if (deps.interceptDatabaseError(getErr)) {
      // #5129: the deletion did not go through, and the read that would say whether that
      // matters is the one that just failed — so we cannot tell a lost deletion from a doc
      // that was already a tombstone (the not-found branch below, which is a success no-op).
      // Report it rather than assume the harmless case; DeleteConflictRead is a separate rung
      // precisely so this uncertainty stays visible in triage.
      //
      // For the same reason this answers DroppedIntoRecovery rather than Written
      // (df-068808a2796e): the caller treats that as "the deletion did not land", which puts
      // the record's `_deleted` mark back. That can be a FALSE NEGATIVE — the tombstone may
      // really be there — and it is the safe direction of the two, the same choice
      // `deleteOutcomeNeedsRestore` already makes for `DeleteOutcome.Unknown`. A mark left
      // standing on a document whose deletion did not land is what destroys the user's data;
      // a mark taken off one whose deletion did land costs a delete that has to be repeated.
      reportWriteDroppedIntoGracefulRecovery(deps, record._id, WriteDropSite.DeleteConflictRead);
      return WriteOutcome.DroppedIntoRecovery;
    }
    if (isPouchNotFound(getErr)) {
      // the winner is already a tombstone (or the doc is gone everywhere): the
      // deletion's goal state is reached, so this is a success no-op.
      deps.clearExpectedRev(record._id);
      deps.log(`retryPutWithMergeAsync: delete of ${record._id} conflicted but the local doc is already deleted/absent — `
        + `deletion goal already reached (attemptedRev=${attemptedRev})`);
      return WriteOutcome.Written;
    }
    // an unexpected (non-not-found) read failure — surface the drop
    deps.logError(`retryPutWithMergeAsync: delete of ${record._id} conflicted and the current doc is unreadable: `
      + `${(getErr as any)?.message || getErr}`);
    return WriteOutcome.Lost;
  }
  // the winner is a LIVE revision — the deletion did NOT land. surface the drop
  // (do not silently claim success, do not clobber the live winner). `current`
  // is read only to log the live winner's rev for diagnostics.
  deps.log(`retryPutWithMergeAsync: delete of ${record._id} conflicted and the current winner is LIVE `
    + `(rev ${current._rev}) — the deletion did not land; surfacing the drop (attemptedRev=${attemptedRev})`);
  return WriteOutcome.Lost;
}

// #3987 A1 — bulk-save conflict recovery.
//
// `saveBulkAsync` used to fire one `bulkDocs` call and only process the rows
// that came back `ok`. A row that failed — most importantly a 409 conflict,
// which happens when pull replication bumps a doc's _rev between the caller
// reading the doc and the bulk write landing — was silently dropped: the
// user's edit or deletion was lost with no retry and no telemetry (audit
// defect D1). This helper walks the bulkDocs result rows and recovers them:
// conflicted rows get one merge-retry (the same proven merge-retry the
// single-doc save path uses), and every row that stays failed is reported
// via trackSilentProblem so a drop is never silent again.

/** One row of a PouchDB `bulkDocs()` result. Success rows have `ok: true`
 *  plus `id`/`rev`; failure rows have `error: true` plus `status`/`name`/
 *  `message` describing why. */
export interface BulkSaveResultRow {
  ok?: boolean;
  error?: boolean;
  status?: number;
  name?: string;
  message?: string;
  id?: string;
  rev?: string;
  /**
   * Set by `recoverBulkSaveFailuresAsync` on a row whose recovery ended in
   * `WriteOutcome.DroppedIntoRecovery`: the failure was taken over by the database
   * recovery, so NOTHING WAS WRITTEN for this row (df-068808a2796e).
   *
   * Such a row still carries `ok: true`, deliberately. Every existing reader of these
   * rows judges by `ok`/`error`, and an ordinary bulk SAVE has always treated a
   * recovery-swallowed write as handled — the in-memory record keeps the user's edit
   * and their next save carries it in. Flipping those rows to `error` would change what
   * every bulk caller sees, which is not what this fixes.
   *
   * It is the DELETE path that cannot live with the conflation, because a deleted object
   * is never saved again. `bulkRowLostTheWrite` reads this field, and `deleteRecordsAsync`
   * is its only caller.
   */
  droppedIntoRecovery?: boolean;
}

/** Dependencies for recoverBulkSaveFailuresAsync, injected so the recovery
 *  logic is a pure function we can unit test (DatabaseWrapper itself cannot
 *  be loaded under node — see the module comment at the top of this file). */
export interface BulkSaveRecoveryDeps {
  /** The wrapper's shared single-doc merge-retry (retryPutWithMergeAsync).
   *  Answers `Written` when the record reached the database, `DroppedIntoRecovery`
   *  when a failure was taken over by the database recovery and nothing was
   *  written, and `Lost` when the write is genuinely gone. */
  retryPutWithMerge: (record: PouchDoc) => Promise<WriteOutcome>;
  trackSilentProblem: (name: string, ctx: Record<string, unknown>) => void;
}

/** True when a bulkDocs result row failed with a revision conflict (409).
 *
 *  A row reports the conflict with the same two fields a caught error does, so the
 *  decision itself lives in `isPouchConflict`. This wrapper stays because it keeps the
 *  TYPED `BulkSaveResultRow` parameter: callers pass a result row, not an `unknown`
 *  error, and losing that type would let any object through. */
export function isBulkRowConflict(row: BulkSaveResultRow): boolean {
  return isPouchConflict(row);
}

/** The `name` saveBulkAsync puts on the failure row it stands in for a record it
 *  refused to send to PouchDB because the record had no usable `_id`. Lives here
 *  so the producer (database-wrapper.ts) and the reporter below agree on one
 *  spelling, and so a test can assert it without loading DatabaseWrapper. */
export const MISSING_ID_ROW_NAME = 'missing_id';

/** True when a document carries an `_id` that PouchDB will actually accept: a
 *  non-empty string. Anything else — absent, undefined, or a non-string — makes
 *  PouchDB invent a random uuid instead, which is never what a caller here wanted.
 *  One definition, because several places ask the same question — including the
 *  three-way merge in `shared/src/deep-helpers.ts`, which asks it of a bare value
 *  rather than of a document. That is why the rule itself lives there and this
 *  only adds the "and there is a document at all" half. */
export function hasUsableDocId(doc: PouchDoc | undefined): boolean {
  return !!doc && isUsableDocId(doc._id);
}

/**
 * Describe a record whose bulk write was dropped, for the telemetry report.
 *
 * When `_id` is missing, fall back to whatever else can name the document: its
 * `type`, its timestamp, the revision it was carrying, and the NAMES of the keys
 * it has (never their values — those are user data). Without that fallback the
 * report is empty for exactly the case it matters in. See
 * `design-docs/sync-integrity.md`, "A record that reaches the save path with no
 * `_id`".
 */
export function describeDroppedBulkRecord(record: PouchDoc | undefined): Record<string, unknown> {
  if (hasUsableDocId(record)) {
    return { docId: record._id };
  }
  if (!record) {
    return { docId: undefined, recordMissing: true };
  }
  const asAny = record as any;
  return {
    docId: undefined,
    idType: typeof asAny._id,
    docType: asAny.type,
    time_utc: asAny.time_utc,
    rev: asAny._rev,
    dbTrans: asAny.db__trans,
    // key names only — values are user data and must not reach telemetry
    keys: Object.keys(record).sort().join(','),
  };
}

/**
 * Recover the failed rows of a `bulkDocs` result (#3987 A1, defect D1).
 * `records[i]` corresponds to `results[i]` — PouchDB returns rows in input
 * order. Conflicted rows are retried through the injected merge-retry; rows
 * that are still lost afterwards (merge-retry failed, or a non-conflict
 * error such as an IndexedDB failure) are reported one-by-one via
 * trackSilentProblem (volume is low, and per-doc context beats a summary).
 * A single bad row must never abort the other rows, so this never throws
 * for a row-level failure.
 *
 * SINGLE TELEMETRY OWNER: this function is the one place that reports a
 * dropped bulk write. Callers of saveBulkAsync must NOT re-report rows from
 * the returned results (saveEntriesAsync used to — that double-counted).
 *
 * A successfully recovered row is REWRITTEN IN PLACE to a success row
 * (`ok: true` + the record's post-merge `rev`; the error fields cleared),
 * so callers reading the returned results see reality: `error: true` rows
 * are exactly the writes that were genuinely lost (and already reported).
 */
export async function recoverBulkSaveFailuresAsync(
  deps: BulkSaveRecoveryDeps,
  records: PouchDoc[],
  results: BulkSaveResultRow[],
): Promise<void> {
  for (let index = 0; index < results.length; index++) {
    const row = results[index];
    if (!row || !row.error) continue;
    const record = records[index];
    const isConflict = isBulkRowConflict(row);
    let outcome = WriteOutcome.Lost;
    if (isConflict && record) {
      outcome = await deps.retryPutWithMerge(record);
    }
    if (outcome !== WriteOutcome.Lost) {
      // if the merge-retry landed, rewrite the row so callers see the write
      // as the success it now is (retryPutWithMerge updated record._rev).
      // CAVEAT: several paths get here WITHOUT a durable put, leaving
      // record._rev at the stale attempted rev: (a) interceptDatabaseError
      // handled the failure, (b) the v2 recovery's `_deleted` no-op (deleting
      // an already-deleted doc — the goal state is reached but nothing was
      // written), and (c) the delete-conflict recovery's same
      // already-deleted-locally no-op (nightly #4010). All mirror the
      // single-doc path's long-standing "handled = don't surface" semantics,
      // so do not read `ok: true` (or its `rev`) here as a durability
      // guarantee. Case (a) is the one that can genuinely lose the user's
      // edit — when the recovery repairs the database in place rather than
      // reloading the page, the app carries on and the write is simply gone.
      // Since #5129 that case reports itself under
      // WRITE_DROPPED_INTO_REPAIR_PROBLEM, so it is measured even though this
      // row still says ok. Cases (b) and (c) lose nothing. (b) holds only
      // because a deletion whose pass-through recovery read FAILED never
      // reaches that no-op: it answers Lost instead, since nothing showed
      // the doc was gone on the server (df-4ebed2f69e4a).
      //
      // Case (a) is now also NAMED on the row, as `droppedIntoRecovery`
      // (df-068808a2796e). The row still says ok, for the reason the field's own
      // comment gives; what reads the field is the DELETE path, which is the one
      // caller that cannot treat a swallowed write as done.
      row.ok = true;
      row.id = record._id;
      row.rev = record._rev;
      if (outcome === WriteOutcome.DroppedIntoRecovery) {
        row.droppedIntoRecovery = true;
      }
      delete row.error;
      delete row.status;
      delete row.name;
      delete row.message;
    } else {
      deps.trackSilentProblem('saveBulkAsync: write dropped', {
        // docId plus, when it is missing, the fields that can still name the
        // document — see describeDroppedBulkRecord for why that matters here
        ...describeDroppedBulkRecord(record),
        status: row.status,
        name: row.name,
        // include the error message so a genuine (non-conflict) failure, e.g.
        // an IndexedDB write error, stays diagnosable from the report alone
        message: row.message,
        retried: isConflict,
      });
    }
  }
}

/**
 * What each record looked like BEFORE a delete marked it, so a delete that does not
 * land can put it back (df-1cb1333d7899).
 *
 * `deleted` is what `_deleted` was. It is captured rather than assumed, because a record
 * can legitimately arrive already marked: `markForDeleteLogEntiresAsync` sets the flag on
 * a whole day's entries and then revives the ones it keeps (the mark-then-revive rebuild
 * in `design-docs/sync-integrity.md`). Clearing the flag unconditionally on a failure
 * would undo that caller's own mark. `undefined` records "the field was not present at
 * all" and is restored as an absent field rather than as `false`, so a document that
 * never carried `_deleted` does not gain one — a stored document's shape is what
 * replicates to the server.
 *
 * WHICH records get restored is a separate question, and this type deliberately does not
 * answer it. An exception can arrive with the deletion already in the local store, because
 * the cache bookkeeping that follows a successful put can throw — so restoring every record
 * would undo deletions that really happened. The wrapper asks the local store what it holds
 * for each document and passes the answer in as the selector (df-7bc2239b5f74).
 */
export interface DeleteRestorePoint {
  deleted: (boolean | undefined)[];
}

/** Read the current `_deleted` of every record, in the order given. */
export function captureDeleteRestorePoint(records: PouchDoc[]): DeleteRestorePoint {
  return {
    deleted: records.map(record => record?._deleted),
  };
}

/**
 * What became of one document a delete tried to remove, as far as anything can tell after
 * the save failed. This is the question the restore has to answer; how it is answered is
 * the caller's business.
 */
export enum DeleteOutcome {
  /** The deletion is recorded — the mark describes reality and must STAY. */
  Recorded = 'recorded',
  /** The deletion did not land — the mark is a lie and must come OFF. */
  NotRecorded = 'notRecorded',
  /** Nothing could be established. Treated as `NotRecorded`, and reported. */
  Unknown = 'unknown',
}

/**
 * Whether this outcome means the delete's mark has to be put back.
 *
 * `Unknown` restores, deliberately. The two readings of "I cannot tell" have very
 * different costs: leaving the mark on can destroy a document the user still has (the
 * caller's next ordinary save writes the deletion through, which is what
 * `df-1cb1333d7899` and `df-7bc2239b5f74` are both about), while taking it off can at
 * worst resurrect one. So an unanswerable case fails towards keeping the user's data.
 */
export function deleteOutcomeNeedsRestore(outcome: DeleteOutcome): boolean {
  return outcome !== DeleteOutcome.Recorded;
}

/** One row of a keyed `allDocs`, as far as this decision needs to read it. */
export interface LocalWinnerRow {
  key?: string;
  id?: string;
  error?: unknown;
  value?: { rev?: string; deleted?: boolean };
}

/**
 * Read one `allDocs` row per record and say what became of each document.
 *
 * The row shape IS the three-way answer, and this is the same reading the pass-through
 * ahead-check and the sync-boot policy already do:
 *   - `value.deleted` is true           -> the local winner is a tombstone: `Recorded`
 *   - `value.rev` with no `deleted`     -> the local winner is a live document: `NotRecorded`
 *   - a row with `error`, or no row     -> this store does not hold it: `Unknown`
 *
 * The last case is UNKNOWN rather than `Recorded` on purpose. During the pass-through
 * window reads route to the remote while writes go to local, so the local store
 * legitimately lacks documents that are alive on the server; calling those deleted would
 * leave the delete's mark on and destroy them at the caller's next ordinary save — the
 * very defect this decision exists to close (df-7bc2239b5f74).
 *
 * Rows are looked up by `key`, never by `id`: a row for a document the store does not hold
 * carries no `id` at all.
 */
export function deleteOutcomesFromLocalRows(
  records: PouchDoc[],
  rowsByKey: Map<string, LocalWinnerRow>,
): DeleteOutcome[] {
  return records.map(record => {
    const row = typeof record?._id === 'string' ? rowsByKey.get(record._id) : undefined;
    if (row?.value?.deleted === true) return DeleteOutcome.Recorded;
    if (typeof row?.value?.rev === 'string') return DeleteOutcome.NotRecorded;
    return DeleteOutcome.Unknown;
  });
}

/**
 * The ids of the records nothing could decide, deduplicated — what the "could not tell"
 * report names. Deduplicated because `deleteRecordAsync` lists the caller's object and the
 * identity map's object for the same document, and one answer covers both.
 */
export function unknownOutcomeDocIds(records: PouchDoc[], outcomes: DeleteOutcome[]): string[] {
  const ids = new Set<string>();
  records.forEach((record, index) => {
    if (outcomes[index] !== DeleteOutcome.Unknown) return;
    if (typeof record?._id === 'string' && record._id.length > 0) ids.add(record._id);
  });
  return [...ids];
}

/** The distinct document ids of these records, in first-seen order. */
export function distinctDocIds(records: PouchDoc[]): string[] {
  const ids = new Set<string>();
  for (const record of records) {
    if (typeof record?._id === 'string' && record._id.length > 0) ids.add(record._id);
  }
  return [...ids];
}

/**
 * Put back the `_deleted` captured by `captureDeleteRestorePoint`, for the records that
 * `shouldRestore` selects (by index). Omitting `shouldRestore` restores all of them.
 *
 * A record whose captured value was `undefined` has the field DELETED rather than set
 * to `false`, so the document goes back to the shape it had.
 */
export function restoreDeletedFlags(
  records: PouchDoc[],
  point: DeleteRestorePoint,
  shouldRestore?: (index: number) => boolean,
): void {
  for (let index = 0; index < records.length; index++) {
    const record = records[index];
    if (!record) continue;
    if (shouldRestore && !shouldRestore(index)) continue;
    const previous = point.deleted[index];
    if (previous === undefined) {
      delete record._deleted;
    } else {
      record._deleted = previous;
    }
  }
}

/**
 * Whether the bulk row at this index is a write that was genuinely lost.
 *
 * `recoverBulkSaveFailuresAsync` rewrites every row it recovered to `ok: true` and
 * leaves `error: true` on exactly the rows it reported as dropped, so a row still
 * marked `error` is a record that was NOT deleted — its mark has to come off. A
 * MISSING row counts as lost too: nothing says that write landed, and leaving the
 * mark on would delete the document at the caller's next ordinary save.
 *
 * A row carrying `droppedIntoRecovery` counts as lost as well, even though it says
 * `ok: true` (df-068808a2796e). That row's failure was taken over by the database
 * recovery, so nothing was written — and the recovery can repair the database in place
 * and let the page carry on, which leaves no reload to wash the standing mark away. The
 * row keeps `ok` because every OTHER reader of these rows is an ordinary bulk save, for
 * which a swallowed write is deliberately treated as handled; see the field's own comment.
 *
 * This answers for a call that RETURNED its rows. A call that threw has no rows to read;
 * there the local store is asked what it holds for each document instead — see
 * `DeleteOutcome`.
 */
export function bulkRowLostTheWrite(results: BulkSaveResultRow[], index: number): boolean {
  const row = results[index];
  return !row || !!row.error || !!row.droppedIntoRecovery;
}
